// Pure, timezone-aware bucketing for the consolidated view (REQ-VIEW-001, DEPRECATED by
// feature 003 — the planner model in ./planner.ts supersedes it; bucketItems + viewHTML are
// removed once the See surface lands). The date helpers below are shared with planner.ts.
// Groups OPEN items by due date into Overdue / Today / This week (+ Later, + No date),
// using real local calendar dates (Pacific/Auckland) via Intl — no naive offset math,
// so DST transitions don't misbucket. Week starts Monday; "this week" runs from tomorrow
// through the coming Sunday. Nothing is silently dropped (far-future → later; undated → undated).

export interface ViewItem {
  id: string;
  title: string;
  status: string;
  due_at: string | null;
  end_at?: string | null; // explicit end, when a range/duration was captured (REQ-CAL-012)
  location?: string | null; // a venue/place, kept out of the title (REQ-CAL-011)
  rrule?: string | null; // RFC5545 recurrence rule when the item repeats (REQ-CAL-011)
  details?: string | null; // free-format notes attached to the item (REQ-ITEM-007)
  type?: string; // task | event | note | grocery
  source?: string | null; // provenance; an "external:*" source renders read-only (REQ-VIEW-007)
  visibility?: string; // "family" (default) | "private" (only the creator; RLS-enforced)
  owner_member_id?: string | null; // legacy "primary" owner (= owners[0]); kept for the commit gate
  owners?: string[]; // all assigned owners (item_owner, REQ-ITEM-006); falls back to owner_member_id
  [k: string]: unknown;
}

export interface Buckets<T> {
  overdue: T[];
  today: T[];
  thisWeek: T[];
  later: T[];
  undated: T[];
}

export function localDateStr(instant: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

// A calendar date (YYYY-MM-DD) as UTC noon — arithmetic in UTC is DST-immune.
function toUtcNoon(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}
export function addDays(dateStr: string, n: number): string {
  const dt = toUtcNoon(dateStr);
  dt.setUTCDate(dt.getUTCDate() + n);
  return localDateStr(dt, "UTC");
}
// Monday = 1 … Sunday = 7 for a calendar date.
export function weekdayMon1(dateStr: string): number {
  const dow = toUtcNoon(dateStr).getUTCDay(); // 0=Sun … 6=Sat
  return dow === 0 ? 7 : dow;
}

export function bucketItems<T extends ViewItem>(
  items: T[],
  nowISO: string,
  tz = "Pacific/Auckland"
): Buckets<T> {
  const today = localDateStr(new Date(nowISO), tz);
  const endOfWeek = addDays(today, 7 - weekdayMon1(today)); // Sunday of the current week
  const out: Buckets<T> = { overdue: [], today: [], thisWeek: [], later: [], undated: [] };

  for (const item of items) {
    if (item.status !== "open") continue; // done/cancelled/draft never show in the view
    if (!item.due_at) {
      out.undated.push(item);
      continue;
    }
    const due = localDateStr(new Date(item.due_at), tz);
    if (due < today) out.overdue.push(item);
    else if (due === today) out.today.push(item);
    else if (due <= endOfWeek) out.thisWeek.push(item);
    else out.later.push(item);
  }
  return out;
}
